import { FC, useState, useRef, useEffect, FormEvent, ChangeEvent } from 'react';
import clsx from 'clsx';
import type { Message, Conversation } from '@theraply/api-client';
import { MessageBubble } from './MessageBubble';
import { TypingIndicator } from './TypingIndicator';
import { Spinner } from '../Spinner';
import { Button } from '../Button';

export interface ChatWindowProps {
  conversation?: Conversation;
  messages: Message[];
  currentUserId: number;
  currentUserType: 'THERAPIST' | 'CONSUMER';
  isLoading?: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  isTyping?: boolean;
  typingUserName?: string;
  onSendMessage: (content: string) => void;
  onLoadMore?: () => void;
  onTypingStart?: () => void;
  onTypingStop?: () => void;
  className?: string;
}

export const ChatWindow: FC<ChatWindowProps> = ({
  conversation,
  messages,
  currentUserId,
  currentUserType,
  isLoading = false,
  isLoadingMore = false,
  hasMore = false,
  isTyping = false,
  typingUserName,
  onSendMessage,
  onLoadMore,
  onTypingStart,
  onTypingStop,
  className,
}) => {
  const [messageInput, setMessageInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Handle scroll to load more messages
  const handleScroll = () => {
    if (!messagesContainerRef.current || !hasMore || isLoadingMore) return;

    const { scrollTop } = messagesContainerRef.current;
    if (scrollTop < 100 && onLoadMore) {
      onLoadMore();
    }
  };

  // Handle input change with typing indicator
  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessageInput(e.target.value);

    // Trigger typing indicator
    if (onTypingStart) {
      onTypingStart();

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Stop typing after 3 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        if (onTypingStop) {
          onTypingStop();
        }
      }, 3000);
    }
  };

  // Handle form submit
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const content = messageInput.trim();
    if (!content || isSending) return;

    setIsSending(true);
    setMessageInput('');

    // Stop typing indicator
    if (onTypingStop) {
      onTypingStop();
    }

    try {
      await onSendMessage(content);
    } catch (error) {
      console.error('Failed to send message:', error);
      // Restore message on error
      setMessageInput(content);
    } finally {
      setIsSending(false);
    }
  };

  // Handle Enter key to send (Shift+Enter for new line)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (!conversation) {
    return (
      <div className={clsx('flex flex-col items-center justify-center h-full bg-gray-50', className)}>
        <svg
          className="w-24 h-24 text-gray-300 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        <h3 className="text-xl font-medium text-gray-900 mb-2">Select a conversation</h3>
        <p className="text-gray-500">Choose a conversation from the list to start messaging</p>
      </div>
    );
  }

  return (
    <div className={clsx('flex flex-col h-full bg-white', className)}>
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-200">
        {conversation.otherParticipantAvatar ? (
          <img
            src={conversation.otherParticipantAvatar}
            alt={conversation.otherParticipantName || 'User'}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-medium">
            {conversation.otherParticipantName
              ? conversation.otherParticipantName.charAt(0).toUpperCase()
              : '?'}
          </div>
        )}

        <div className="flex-1">
          <h2 className="font-semibold text-gray-900">
            {conversation.otherParticipantName || 'Unknown User'}
          </h2>
          {conversation.otherParticipantOnline && (
            <p className="text-sm text-green-600">Online</p>
          )}
        </div>
      </div>

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        {/* Load more indicator */}
        {isLoadingMore && (
          <div className="flex justify-center py-4">
            <Spinner size="sm" />
          </div>
        )}

        {/* Loading state */}
        {isLoading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            {/* Messages */}
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <p className="text-gray-500 mb-4">No messages yet</p>
                <p className="text-sm text-gray-400">Send a message to start the conversation</p>
              </div>
            ) : (
              <>
                {[...messages].reverse().map((message, index, arr) => {
                  const prevMessage = arr[index - 1];
                  const showAvatar =
                    !prevMessage || prevMessage.senderId !== message.senderId;

                  return (
                    <MessageBubble
                      key={message.id}
                      message={message}
                      isOwnMessage={
                        message.senderId === currentUserId &&
                        message.senderType === currentUserType
                      }
                      showAvatar={showAvatar}
                      showSender={showAvatar}
                    />
                  );
                })}
              </>
            )}

            {/* Typing indicator */}
            {isTyping && (
              <TypingIndicator name={typingUserName || conversation.otherParticipantName} />
            )}

            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Message input */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200">
        <div className="flex gap-2">
          <textarea
            value={messageInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
            className="flex-1 resize-none rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            style={{ maxHeight: '120px' }}
          />
          <Button
            type="submit"
            disabled={!messageInput.trim() || isSending}
            isLoading={isSending}
            size="md"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-2">Press Enter to send, Shift+Enter for new line</p>
      </form>
    </div>
  );
};

ChatWindow.displayName = 'ChatWindow';
